<script setup lang="ts">
// #751: いいね（♥）／お気に入り（★）はログイン必須。未ログインで押したときの
// サインイン導線ダイアログ。全露出面から同じものを 1 つだけ開くため、状態は
// useSignInCta（useState）で共有し、本体は app.vue に 1 度だけマウントする。
//
// chrome は「明快さ最優先」。羊皮紙 Dialog（voice）は使わず、プレーンな Vuetify
// ダイアログで用件（サインインすると何ができるか）と CTA だけを平叙で示す。
// コピーは #751 で確定（情緒表現は不可）。順序は frontend_ui_patterns.md の
// 「CTA と目的をはじめに示す」（別画面遷移型なので CTA を先頭寄りに）に従う。
import { mdiHeart, mdiStar } from '@mdi/js'
import { setPostLoginRedirect } from '~/composables/auth/postLoginRedirect'

const { isOpen, kind, close } = useSignInCta()
const route = useRoute()

const headline = computed(() =>
  kind.value === 'favorite'
    ? 'お気に入りに入れるにはサインイン'
    : 'いいねするにはサインイン',
)

const icon = computed(() => (kind.value === 'favorite' ? mdiStar : mdiHeart))
const iconColor = computed(() => (kind.value === 'favorite' ? '#ffca28' : '#ef9a9a'))

// サインイン成功後は元の画面へ戻す（#548 と同じ sessionStorage 機構）。
// メール/PW（useAuthFlow）・Google（callback）どちらの経路でも消費される。
const goToAuth = (path: '/signin' | '/signup') => {
  setPostLoginRedirect(route.fullPath)
  close()
  navigateTo(path)
}
</script>

<template>
  <v-dialog
    v-model="isOpen"
    max-width="380"
    :aria-label="headline"
  >
    <v-card class="signin-cta-card pa-2">
      <div class="text-center pt-4">
        <v-icon
          :icon="icon"
          :color="iconColor"
          size="40"
        />
      </div>

      <v-card-title class="signin-cta-title text-center text-wrap">
        {{ headline }}
      </v-card-title>

      <v-card-text class="signin-cta-sub text-center text-medium-emphasis pb-2">
        無料。終わったらこの作品に戻れます。
      </v-card-text>

      <v-card-actions class="signin-cta-actions flex-column px-4 pb-4">
        <v-btn
          color="primary"
          variant="flat"
          block
          size="large"
          @click="goToAuth('/signin')"
        >
          サインイン
        </v-btn>
        <v-btn
          variant="text"
          block
          class="mt-2"
          @click="goToAuth('/signup')"
        >
          はじめての方（無料登録）
        </v-btn>
        <v-btn
          variant="text"
          block
          size="small"
          class="mt-1 text-medium-emphasis"
          @click="close"
        >
          今はしない
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.signin-cta-title {
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.4;
}

.signin-cta-sub {
  font-size: 0.95rem;
}

/* v-card-actions は既定で子ボタン間に margin を入れるが、block の縦積みでは
   不要なので詰める（各ボタンの mt-* で間隔を制御する）。 */
.signin-cta-actions :deep(.v-btn + .v-btn) {
  margin-inline-start: 0;
}
</style>
